import { HERO_CLASSES, HERO_EPITHETS, HERO_FIRST_NAMES, levelForXp } from '../../data/heroes.ts';
import { powerOrNull } from '../../data/powers.ts';
import { unitTypeById, unitTypeIndex } from '../../data/units.ts';
import { rankHpMul } from '../../data/ranks.ts';
import type { CompanionHero } from '../herotypes.ts';
import { EV, LogTone, pushEvent, pushLog } from '../events.ts';
import { alliedWith, Phase, Team, UnitState } from '../types.ts';
import { companyCommanded, findCompany, spawnUnit, type World } from '../world.ts';
import { createCompany } from './companies.ts';
import { HERO_ABILITY_COOLDOWN_MUL, HERO_ABILITY_RANGE, HERO_HP_PER_LEVEL, HERO_REVIVE_SECONDS } from './hero.ts';
import { unleashPower } from './powers.ts';

export const COMPANION_RECRUIT_GOLD = 250;
export const COMPANION_RECRUIT_COST = COMPANION_RECRUIT_GOLD;
export const COMPANION_REVIVE_GOLD = 125;
export const MAX_COMPANIONS = 3;

export function createCompanion(id: number, classId: string, seed: number, armyId = -1): CompanionHero {
  const s = ((seed >>> 0) ^ Math.imul(id, 0x9e3779b1)) >>> 0;
  return { id, classId, name: `${HERO_FIRST_NAMES[s % HERO_FIRST_NAMES.length]} ${HERO_EPITHETS[(s >>> 7) % HERO_EPITHETS.length]}`,
    level: 1, xp: 0, cooldowns: {}, fallen: false, reviveT: 0, unitH: -1, companyId: -1, armyId };
}

export function cloneCompanion(h: CompanionHero): CompanionHero {
  return { ...h, cooldowns: { ...h.cooldowns } };
}

export function companionUnit(w: World, h: CompanionHero): number {
  return h.unitH < 0 ? -1 : w.units.resolve(h.unitH);
}

export function companionByCompany(w: World, companyId: number): CompanionHero | undefined {
  return companyId < 0 ? undefined : w.companions.find(h => h.companyId === companyId);
}

/** No shared passive bag: a companion contributes their own body and active powers. */
export function fieldCompanionAt(w: World, h: CompanionHero, x: number, y: number, team: number = Team.Player): boolean {
  const existing = w.companions.find(v => v.id === h.id);
  const character = existing ?? h;
  if (character.fallen || character.unitH >= 0) return false;
  const cls = HERO_CLASSES.find(c => c.id === character.classId);
  if (!cls || !Number.isFinite(x) || !Number.isFinite(y) || !w.map.inBounds(Math.floor(x), Math.floor(y))) return false;
  const typeIdx = unitTypeIndex(cls.unitId), i = spawnUnit(w, typeIdx, team, x, y);
  if (i < 0) return false;
  const u = w.units;
  u.heroId[i] = HERO_CLASSES.indexOf(cls);
  u.maxHp[i] *= cls.hp * (1 + HERO_HP_PER_LEVEL * (character.level - 1));
  u.hp[i] = u.maxHp[i]; u.speed[i] *= cls.speed; u.morale[i] = 1;
  const c = createCompany(w, typeIdx, team, u.x[i], u.y[i]);
  c.name = character.name; c.maxSize = 1;
  u.companyId[i] = c.id; u.slotIdx[i] = 0; c.members.push(u.handle(i));
  character.unitH = u.handle(i); character.companyId = c.id;
  if (!existing) w.companions.push(character);
  w.nextCompanionId = Math.max(w.nextCompanionId, character.id + 1);
  return true;
}

export interface CompanionAbilitySlot { powerId: string; name: string; minLevel: number; learned: boolean }

export function companionAbilitySlots(_w: World, h: CompanionHero): CompanionAbilitySlot[] {
  const cls = HERO_CLASSES.find(c => c.id === h.classId);
  if (!cls) return [];
  return cls.skills.filter(s => s.grantsPower)
    .sort((a, b) => a.minLevel - b.minLevel || a.row - b.row || a.col - b.col || (a.id < b.id ? -1 : 1))
    .slice(0, 5).map((s, n) => ({ powerId: s.grantsPower!, name: s.name, minLevel: 1 + n * 2, learned: h.level >= 1 + n * 2 }));
}

export function companionAbilityError(w: World, id: number, powerId: string): string {
  const h = w.companions.find(v => v.id === id);
  if (!h) return 'No such hero.';
  if (h.fallen) return 'This hero is wounded.';
  const i = companionUnit(w, h), c = findCompany(w, h.companyId);
  if (i < 0 || w.units.hp[i] <= 0 || !c || !companyCommanded(w, c)) return 'This hero is not on the field.';
  if (w.battle?.deploying) return 'Finish deployment first.';
  if (w.gameOver || (w.battle && w.battle.outcome !== 0)) return 'The fighting is over.';
  if (w.units.stunT[i] > 0 || w.units.state[i] === UnitState.Routing) return 'This hero cannot act yet.';
  const slot = companionAbilitySlots(w, h).find(s => s.powerId === powerId);
  if (!slot) return 'Not this hero’s ability.';
  if (!slot.learned) return `Needs level ${slot.minLevel}.`;
  if ((h.cooldowns[powerId] ?? 0) > 0) return 'Still cooling.';
  return '';
}

export function castCompanionAbility(w: World, id: number, powerId: string, x: number, y: number): boolean {
  if (companionAbilityError(w, id, powerId)) return false;
  const h = w.companions.find(v => v.id === id)!, i = companionUnit(w, h);
  if (!Number.isFinite(x) || !Number.isFinite(y) || !w.map.inBounds(Math.floor(x), Math.floor(y))) return false;
  const dx = x - w.units.x[i], dy = y - w.units.y[i], distance = Math.hypot(dx, dy);
  if (distance > HERO_ABILITY_RANGE) return false;
  const cooldown = unleashPower(w, powerId, x, y, distance > 0.01 ? dx / distance : Math.cos(w.units.facing[i]), distance > 0.01 ? dy / distance : Math.sin(w.units.facing[i]), w.units.team[i]);
  if (cooldown <= 0) return false;
  h.cooldowns[powerId] = cooldown * HERO_ABILITY_COOLDOWN_MUL;
  pushLog(w.events, `${h.name}: ${powerOrNull(powerId)?.name ?? powerId}.`, LogTone.Lore);
  return true;
}

export function companionRecruitError(w: World, classId: string): string {
  const cls = HERO_CLASSES.find(c => c.id === classId);
  if (!cls) return 'No such hero class.';
  if (cls.factions.length && !cls.factions.includes(w.faction.id)) return 'This hero serves another people.';
  if (w.battle) return 'Recruit heroes in a settlement.';
  if (w.phase !== Phase.Build) return 'Recruit between sieges.';
  if (w.companions.some(h => h.classId === classId)) return 'A hero of this class already serves here.';
  if (w.companions.length >= MAX_COMPANIONS) return `Already supporting ${MAX_COMPANIONS} heroes.`;
  if (w.res.gold < COMPANION_RECRUIT_GOLD) return `Needs ${COMPANION_RECRUIT_GOLD} gold.`;
  return '';
}

export function recruitCompanion(w: World, classId: string): boolean {
  if (companionRecruitError(w, classId)) return false;
  const h = createCompanion(w.nextCompanionId, classId, w.config.seed);
  if (!fieldCompanionAt(w, h, w.map.hearthX + 1.5 + w.companions.length, w.map.hearthY + 3.5)) return false;
  w.res.gold -= COMPANION_RECRUIT_GOLD;
  pushLog(w.events, `${h.name} joins as a ${HERO_CLASSES.find(c => c.id === classId)!.name}.`, LogTone.Good);
  return true;
}

export function companionReviveError(w: World, id: number): string {
  const h = w.companions.find(v => v.id === id);
  if (!h?.fallen) return 'This hero is not wounded.';
  if (w.battle) return 'Recover in a settlement.';
  if (h.reviveT > 0) return `Recovering: ${Math.ceil(h.reviveT)}s.`;
  if (w.res.gold < COMPANION_REVIVE_GOLD) return `Needs ${COMPANION_REVIVE_GOLD} gold.`;
  return '';
}

export function reviveCompanion(w: World, id: number): boolean {
  if (companionReviveError(w, id)) return false;
  const h = w.companions.find(v => v.id === id)!;
  h.fallen = false;
  if (!fieldCompanionAt(w, h, w.map.hearthX + 1.5, w.map.hearthY + 3.5)) { h.fallen = true; return false; }
  w.res.gold -= COMPANION_REVIVE_GOLD; h.reviveT = 0;
  pushLog(w.events, `${h.name} returns to service.`, LogTone.Good);
  return true;
}

export function awardCompanionXp(w: World, amount: number, team: number = Team.Player): void {
  if (!Number.isFinite(amount) || amount <= 0) return;
  for (const h of w.companions) {
    const body = companionUnit(w, h);
    if (h.fallen || body < 0 || w.units.hp[body] <= 0 || !alliedWith(w.units.team[body], team)) continue;
    h.xp += amount;
    const next = levelForXp(h.xp);
    if (next <= h.level) continue;
    h.level = next;
    const i = companionUnit(w, h), cls = HERO_CLASSES.find(c => c.id === h.classId);
    if (i >= 0 && cls) {
      const u = w.units, fraction = u.maxHp[i] > 0 ? u.hp[i] / u.maxHp[i] : 1;
      const hpMods = u.team[i] === Team.Player
        ? (w.faction.bonuses.unitHp ?? 1) * w.mods.unitHp * rankHpMul(w.unitRanks[cls.unitId] ?? 0) : 1;
      u.maxHp[i] = unitTypeById(cls.unitId).hp * cls.hp * (1 + HERO_HP_PER_LEVEL * (h.level - 1)) * hpMods;
      u.hp[i] = u.maxHp[i] * fraction;
      pushEvent(w.events, EV.Levelup, u.x[i], u.y[i], h.level, 0, u.team[i], 0, 0, h.classId);
    }
    pushLog(w.events, `${h.name} reaches level ${h.level}.`, LogTone.Good);
  }
}

export function stepCompanions(w: World, dt: number): void {
  for (const h of w.companions) {
    for (const id of Object.keys(h.cooldowns)) h.cooldowns[id] = Math.max(0, h.cooldowns[id] - dt);
    if (h.unitH >= 0 && companionUnit(w, h) < 0) {
      h.unitH = -1; h.fallen = true; h.reviveT = HERO_REVIVE_SECONDS;
      pushLog(w.events, `${h.name} is wounded and must recover.`, LogTone.Dire);
    }
    // Attached heroes recover under the campaign's safe-holding rule. A long
    // tactical battle must not consume the recovery owed after its verdict.
    if (h.fallen && h.armyId < 0 && h.reviveT > 0) h.reviveT = Math.max(0, h.reviveT - dt);
  }
}
